import React from 'react';

/**
 * Seletor de horário de nascimento em formato 24h (dois <select>: hora e minuto).
 *
 * Substitui o <input type="time"> nativo, que em navegador/celular com locale 12h
 * renderiza um seletor com AM/PM — e, se a pessoa escolhe hora e minuto mas NÃO
 * marca AM/PM, o `.value` volta vazio sem nenhum aviso. Já causou bug real (usuária
 * preencheu a hora no cadastro, o campo salvou vazio, a leitura saiu "sem
 * ascendente"). Ver PR feat/hora-24h-e-nome-completo.
 *
 * O valor trafega sempre pela prop `value` (componente 100% controlado):
 *   - ""        → nada preenchido
 *   - "HH:MM"   → completo e válido
 *   - "HH:" / ":MM" → parcial (só um dos dois selects) — o formulário-pai detecta
 *     com `isPartialTime()` e bloqueia o envio com um aviso, em vez de descartar
 *     em silêncio.
 */

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** `true` quando o valor é um "HH:MM" válido (00:00–23:59). */
export const isCompleteTime = (v: string | null | undefined): boolean => !!v && TIME_RE.test(v);

/** `true` quando há algo preenchido mas ainda não é um "HH:MM" completo. */
export const isPartialTime = (v: string | null | undefined): boolean => !!v && !TIME_RE.test(v);

interface BirthTimeSelectProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Classe visual aplicada em cada <select> (padding, borda, fundo). */
  selectClassName?: string;
  /** Estilo inline de cada <select> (ex.: fundo translúcido). */
  selectStyle?: React.CSSProperties;
  /** Estilo inline de cada <option> (fundo escuro pro dropdown ficar legível). */
  optionStyle?: React.CSSProperties;
  /** Realça as bordas em vermelho (usado quando o pai marca o campo como parcial). */
  invalid?: boolean;
}

const BirthTimeSelect: React.FC<BirthTimeSelectProps> = ({
  value,
  onChange,
  disabled,
  selectClassName = '',
  selectStyle,
  optionStyle,
  invalid,
}) => {
  const [rawH, rawM = ''] = (value || '').split(':');
  const hour = HOURS.includes(rawH) ? rawH : '';
  const minute = MINUTES.includes(rawM) ? rawM : '';

  const emit = (h: string, m: string) => {
    onChange(!h && !m ? '' : `${h}:${m}`);
  };

  const cls = `${selectClassName}${invalid ? ' border-red-400' : ''}`;

  return (
    <div className="grid grid-cols-2 gap-2">
      <select
        aria-label="Hora de nascimento"
        value={hour}
        disabled={disabled}
        onChange={(e) => emit(e.target.value, minute)}
        className={cls}
        style={selectStyle}
      >
        <option value="" style={optionStyle}>Hora</option>
        {HOURS.map((h) => (
          <option key={h} value={h} style={optionStyle}>{h}h</option>
        ))}
      </select>
      <select
        aria-label="Minuto de nascimento"
        value={minute}
        disabled={disabled}
        onChange={(e) => emit(hour, e.target.value)}
        className={cls}
        style={selectStyle}
      >
        <option value="" style={optionStyle}>Minuto</option>
        {MINUTES.map((m) => (
          <option key={m} value={m} style={optionStyle}>{m}</option>
        ))}
      </select>
    </div>
  );
};

export default BirthTimeSelect;
